export type EntityType =
  | "tenants"
  | "restaurants" // scoped-path-ok: counter entity key (stored doc id)
  | "domain_mappings" // @deprecated - use "domains" for unified domain management
  | "domains"
  | "integrations"
  | "system_roles"
  | "allergy_categories"
  | "ingredients"
  | "items"
  | "courses"
  | "sales_categories"
  | "tax_rates"
  | "modifier_groups"
  | "modifiers"
  | "menu_groups"
  | "tags"
  | "prep_stations"
  | "printers"
  | "cash_drawers"
  | "kitchen_buzzers"
  | "employees"
  | "labor_warnings"
  | "schedules"
  | "shift_requests"
  | "shifts"
  | "shift_tasks"
  | "job_titles"
  | "departments"
  | "orders"
  | "checks"
  | "carts"
  | "reviews"
  | "media"
  | "table_layouts"
  | "tables"
  | "revenue_centers"
  | "table_assignments"
  | "table_merges"
  | "waitlist_entries"
  | "seated_customers"
  | "reservations"
  | "devices"
  | "notifications"
  | "platform_requests"
  | "permission_groups"
  | "permission_sub_accesses"
  | "job_permissions"
  | "support_sessions"
  | "discounts"
  | "void_reasons"
  | "payments"
  | "time_entries"
  | "transfer_records"
  | "gift_cards"
  | "gift_card_transactions"
  | "business_types"
  | "business_sub_categories"
  | "network_configs"
  | "bookable_services"
  | "appointments";

/**
 * Platform entity types (globally unique across the platform, stored in top-level collections)
 */
export type PlatformEntityType =
  | "tenants"
  | "restaurants" // scoped-path-ok: counter entity key (stored doc id)
  | "domain_mappings"
  | "domains"
  | "integrations"
  | "system_roles"
  | "allergy_categories"
  | "ingredients"
  | "platform_requests"
  | "permission_groups"
  | "permission_sub_accesses"
  | "support_sessions"
  | "business_types"
  | "business_sub_categories";

/**
 * Restaurant-level entity types — every EntityType that is NOT platform-level.
 *
 * This exists so the counter services can type their COLLECTION_PATH_MAP as a
 * TOTAL `Record<RestaurantEntityType, …>`, which makes `tsc` the guard against
 * the defect that has now shipped four times: an EntityType gains an
 * `ENTITY_ID_RANGES` entry but not a collection-path entry, so
 * `generateNextId(…)` evaluates `undefined(…)` → TypeError → CounterError →
 * 500 on every create. It broke `cash_drawers`, then `schedules` /
 * `shift_requests` / `labor_warnings`, then `bookable_services` / `appointments`
 * (core) and `tables` / `shift_tasks` (client).
 *
 * A comment in counterService.ts claimed a test named
 * `TestEveryRestaurantEntityHasACollectionPath` pinned this. No such test ever
 * existed — a repo-wide grep returned only that comment. A total Record is a
 * stronger guard than the test would have been: it cannot be skipped, cannot
 * silently pass on an empty fixture, and fails at build time rather than at the
 * first customer create.
 */
export type LocationEntityType = Exclude<EntityType, PlatformEntityType>;

/**
 * Determines if an entity type is platform-level (globally unique) or restaurant-level
 */
export const isPlatformEntity = (
  entityType: EntityType,
): entityType is PlatformEntityType => {
  return (
    entityType === "tenants" ||
    entityType === "restaurants" || // scoped-path-ok: counter entity key (stored doc id)
    entityType === "domain_mappings" ||
    entityType === "domains" ||
    entityType === "integrations" ||
    entityType === "system_roles" ||
    entityType === "allergy_categories" ||
    entityType === "ingredients" ||
    entityType === "platform_requests" ||
    entityType === "permission_groups" ||
    entityType === "permission_sub_accesses" ||
    entityType === "support_sessions" ||
    entityType === "business_types" ||
    entityType === "business_sub_categories"
  );
};

/**
 * Entity ID range configuration.
 * Canonical definition — both client and core counter services import this.
 */
export interface EntityIdConfig {
  readonly startId: number;
  readonly prefix: string;
  readonly idField: string;
}

/**
 * Legacy camelCase → snake_case mapping for backwards-compatible counter doc migration.
 * When the counter service reads a counter doc, it checks the new snake_case name first,
 * then falls back to the legacy camelCase name and migrates it in-place.
 */
export const LEGACY_ENTITY_TYPE_MAP: Record<string, EntityType> = {
  domainMappings: "domain_mappings",
  salesCategories: "sales_categories",
  taxRates: "tax_rates",
  modifierGroups: "modifier_groups",
  menuGroups: "menu_groups",
  prepStations: "prep_stations",
  jobTitles: "job_titles",
  tableLayouts: "table_layouts",
  revenueCenters: "revenue_centers",
  tableAssignments: "table_assignments",
  tableMerges: "table_merges",
  waitlistEntries: "waitlist_entries",
  seatedCustomers: "seated_customers",
  jobPermissions: "job_permissions",
  giftCards: "gift_cards",
  giftCardTransactions: "gift_card_transactions",
};

/** Reverse lookup: snake_case → legacy camelCase */
export const REVERSE_LEGACY_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(LEGACY_ENTITY_TYPE_MAP).map(([k, v]) => [v, k]),
);

/**
 * Canonical ID ranges for all entity types.
 * Platform-level entities have high startIds (globally unique).
 * Restaurant-level entities start at 1 (unique within a restaurant).
 */
export const ENTITY_ID_RANGES: Record<EntityType, EntityIdConfig> = {
  // Platform-level entities (globally unique across platform)
  tenants: { startId: 10000001, prefix: "TENANT", idField: "tenantId" },
  restaurants: { startId: 20000001, prefix: "REST", idField: "restaurantId" },
  domain_mappings: { startId: 30000001, prefix: "DOMAIN", idField: "domainId" },
  domains: { startId: 35000001, prefix: "DOMAIN", idField: "domainId" },
  integrations: { startId: 40000001, prefix: "INT", idField: "integrationId" },
  system_roles: { startId: 50000001, prefix: "ROLE", idField: "systemRoleId" },
  allergy_categories: { startId: 60000001, prefix: "ALLERGY", idField: "allergyCategoryId" },
  ingredients: { startId: 70000001, prefix: "INGR", idField: "ingredientId" },
  platform_requests: { startId: 80000001, prefix: "PREQ", idField: "requestId" },
  permission_groups: { startId: 85000001, prefix: "PERMGRP", idField: "permissionGroupId" },
  permission_sub_accesses: { startId: 86000001, prefix: "PERMSUB", idField: "permissionSubAccessId" },
  support_sessions: { startId: 90000001, prefix: "SUPPORT", idField: "sessionId" },
  business_types: { startId: 91000001, prefix: "BTYPE", idField: "businessTypeId" },
  business_sub_categories: { startId: 92000001, prefix: "BSUBCAT", idField: "businessSubCategoryId" },
  // Managed-router network configs (per restaurant, immutable + versioned).
  network_configs: { startId: 93000001, prefix: "NETCFG", idField: "networkConfigId" },

  // Restaurant-level entities (unique within restaurant)
  items: { startId: 1, prefix: "ITEM", idField: "itemId" },
  courses: { startId: 1, prefix: "COURSE", idField: "courseId" },
  sales_categories: { startId: 1, prefix: "SALESCAT", idField: "salesCategoryId" },
  tax_rates: { startId: 1, prefix: "TAXRATE", idField: "taxRateId" },
  modifier_groups: { startId: 1, prefix: "MODGROUP", idField: "modifierGroupId" },
  modifiers: { startId: 1, prefix: "MOD", idField: "modifierId" },
  // RecipeOps (Phase 14-R) operational entities (recipe_items / inventory_items /
  // modifier_ingredients) intentionally have NO id range: identity is a randomUUID
  // `guid` (= Firestore doc id), never a numeric counter (ro-5 / mi-1).
  menu_groups: { startId: 1, prefix: "MENUGROUP", idField: "menuGroupId" },
  tags: { startId: 1, prefix: "TAG", idField: "tagId" },
  prep_stations: { startId: 1, prefix: "PREPSTATION", idField: "prepStationId" },
  printers: { startId: 1, prefix: "PRINTER", idField: "printerId" },
  // Cash drawers are their own entity, not an array element on a printer: a
  // drawer outlives the printer it is wired to (swap failed hardware and the
  // history must survive), and the financial plane addresses it by `guid`.
  cash_drawers: { startId: 1, prefix: "CASHDRAWER", idField: "cashDrawerId" },
  kitchen_buzzers: { startId: 1, prefix: "KITCHENBUZZER", idField: "kitchenBuzzerId" },
  employees: { startId: 1, prefix: "EMP", idField: "employeeId" },
  labor_warnings: { startId: 1, prefix: "WARN", idField: "warningId" },
  schedules: { startId: 1, prefix: "SCHED", idField: "scheduleId" },
  shift_requests: { startId: 1, prefix: "SHIFTREQ", idField: "requestId" },
  shifts: { startId: 1, prefix: "SHIFT", idField: "shiftId" },
  shift_tasks: { startId: 1, prefix: "SHIFTTASK", idField: "shiftTaskId" },
  job_titles: { startId: 1, prefix: "JOB", idField: "jobId" },
  departments: { startId: 1, prefix: "DEPT", idField: "departmentId" },
  orders: { startId: 1, prefix: "ORDER", idField: "orderId" },
  checks: { startId: 1, prefix: "CHECK", idField: "checkId" },
  carts: { startId: 1, prefix: "CART", idField: "cartId" },
  reviews: { startId: 1, prefix: "REVIEW", idField: "reviewId" },
  media: { startId: 1, prefix: "MEDIA", idField: "mediaId" },
  table_layouts: { startId: 1, prefix: "LAYOUT", idField: "layoutId" },
  tables: { startId: 1, prefix: "TABLE", idField: "tableId" },
  revenue_centers: { startId: 1, prefix: "REVCENTER", idField: "revenueCenterId" },
  table_assignments: { startId: 1, prefix: "ASSIGN", idField: "assignmentId" },
  table_merges: { startId: 1, prefix: "MERGE", idField: "mergeId" },
  waitlist_entries: { startId: 1, prefix: "WAITLIST", idField: "waitlistId" },
  seated_customers: { startId: 1, prefix: "SEATED", idField: "seatedId" },
  reservations: { startId: 1, prefix: "RESERV", idField: "reservationId" },
  devices: { startId: 1, prefix: "DEVICE", idField: "deviceId" },
  notifications: { startId: 1, prefix: "NOTIF", idField: "notificationId" },
  job_permissions: { startId: 1, prefix: "JOBPERM", idField: "jobPermissionId" },
  discounts: { startId: 1, prefix: "DISCOUNT", idField: "discountId" },
  void_reasons: { startId: 1, prefix: "VOIDREASON", idField: "voidReasonId" },
  payments: { startId: 1, prefix: "PAY", idField: "paymentId" },
  time_entries: { startId: 1, prefix: "TIME", idField: "timeEntryId" },
  transfer_records: { startId: 1, prefix: "TRANSFER", idField: "transferId" },
  // Appointments (appointment-style scheduling) — restaurant-level entities.
  bookable_services: { startId: 1, prefix: "SVC", idField: "bookableServiceId" },
  appointments: { startId: 1, prefix: "APPT", idField: "appointmentId" },

  // Tenant-level entities (unique within tenant, not platform or restaurant)
  gift_cards: { startId: 1, prefix: "GCARD", idField: "giftCardId" },
  gift_card_transactions: { startId: 1, prefix: "GCTXN", idField: "giftCardTransactionId" },
};

export interface ICounter {
  // Core identification
  readonly entityType: EntityType;
  readonly nextId: number;

  // Metadata
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface ICounterData {
  entityType: EntityType;
  nextId?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export class Counter implements ICounter {
  readonly entityType: EntityType;
  readonly nextId: number;

  // Metadata
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: ICounterData) {
    this.entityType = data.entityType;
    this.nextId = data.nextId ?? 0;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  toJSON(): ICounter {
    return {
      entityType: this.entityType,
      nextId: this.nextId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, unknown>): Counter {
    if (!data.entityType)
      throw new Error("Counter.fromJSON: entityType is missing.");

    // Helper function to convert Firestore timestamps to Date objects
    const convertTimestamp = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      // Check if it's a Firestore Timestamp object
      if (timestamp && typeof timestamp.toDate === "function") {
        return timestamp.toDate();
      }
      // Check if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse as string/number
      return new Date(timestamp);
    };

    return new Counter({
      entityType: data.entityType as EntityType,
      nextId: data.nextId as number | undefined,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
}
